/** Resource server name badges + query-type badges for dataset catalogue cards. */
export interface DatasetResourceDisplayTags {
  resourceServerNames: string[];
  queryTypes: string[];
}

function normalizeTag(value: unknown): string {
  return String(value ?? '').trim().toUpperCase();
}

function collectQueryTypes(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map(normalizeTag).filter(Boolean);
  }
  if (raw == null || raw === '') {
    return [];
  }
  const single = normalizeTag(raw);
  return single ? [single] : [];
}

/**
 * Extracts resource server names and query types for dataset metadata chips.
 * Uses query types only (not access types) and omits query types that duplicate a server name.
 */
export function extractDatasetResourceDisplayTags(
  item: Record<string, unknown> | null | undefined,
): DatasetResourceDisplayTags {
  const resourceServerNames: string[] = [];
  const queryTypesSet = new Set<string>();

  const rs = item?.['resourceServer'];
  if (rs) {
    const servers = Array.isArray(rs) ? rs : [rs];
    for (const server of servers) {
      if (!server || typeof server !== 'object') {
        continue;
      }
      const record = server as Record<string, unknown>;
      for (const key of ['name', 'type'] as const) {
        const val = normalizeTag(record[key]);
        if (val && !resourceServerNames.includes(val)) {
          resourceServerNames.push(val);
        }
      }
      collectQueryTypes(record['queryTypes'] ?? record['queryType']).forEach((qt) =>
        queryTypesSet.add(qt),
      );
    }
  }

  collectQueryTypes(item?.['queryTypes'] ?? item?.['queryType']).forEach((qt) =>
    queryTypesSet.add(qt),
  );

  const serverNameSet = new Set(resourceServerNames);
  const queryTypes = Array.from(queryTypesSet).filter((qt) => !serverNameSet.has(qt));

  return { resourceServerNames, queryTypes };
}
